import { useMemo, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, Influencer } from '../api';
import Layout from '../components/Layout';
import DashboardView, { type DashboardDataFetcher } from '../components/DashboardView';

export default function AdminInfluencerView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const influencerId = parseInt(id || '0');

  const [influencer, setInfluencer] = useState<Influencer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load influencer data
  useEffect(() => {
    if (!influencerId || isNaN(influencerId)) {
      setError('Invalid influencer ID');
      setLoading(false);
      return;
    }

    const loadInfluencer = async () => {
      try {
        const { influencer: inf } = await api.getInfluencer(influencerId);
        setInfluencer(inf);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load influencer');
      } finally {
        setLoading(false);
      }
    };

    loadInfluencer();
  }, [influencerId]);

  // Create data fetcher for admin influencer view endpoints
  const dataFetcher: DashboardDataFetcher = useMemo(
    () => ({
      getMetrics: (range) => api.getAdminInfluencerMetrics(influencerId, range),
      getMetricsByState: (range) => api.getAdminInfluencerMetricsByState(influencerId, range),
      getOrders: (params) => api.getAdminInfluencerOrders(influencerId, params),
      getOrderDetail: (shopifyOrderId) => api.getAdminInfluencerOrderDetail(influencerId, shopifyOrderId),
    }),
    [influencerId]
  );

  const handleBack = () => {
    navigate('/admin/influencers');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-accent-500/30 border-t-accent-500 rounded-full animate-spin" />
            <p className="text-ink-400">Loading influencer...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !influencer) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center py-20">
          <div className="p-6 rounded-lg bg-accent-500/10 border border-accent-500/20 text-center max-w-md">
            <svg
              className="w-12 h-12 text-accent-400 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <p className="text-accent-400 font-medium mb-2">
              {error || 'Influencer not found'}
            </p>
            <button
              onClick={handleBack}
              className="mt-4 px-4 py-2 bg-ink-800 hover:bg-ink-700 text-ink-200 rounded-lg transition-colors"
            >
              Back to Influencers
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Admin View Banner */}
      <div className="mb-6 p-4 rounded-lg bg-royal-500/10 border border-royal-500/30 flex items-center gap-3">
        <svg
          className="w-5 h-5 text-royal-400 flex-shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
          />
        </svg>
        <div className="flex-1">
          <p className="text-royal-300 font-medium">
            Viewing as: <span className="text-royal-200">{influencer.stage_name}</span>
          </p>
          <p className="text-royal-400/70 text-sm">
            This is a read-only view of what the influencer sees in their dashboard.
          </p>
        </div>
      </div>

      <DashboardView
        dataFetcher={dataFetcher}
        title={`${influencer.stage_name}'s Dashboard`}
        subtitle={`Viewing as admin • ${influencer.first_name} ${influencer.last_name}`}
        readOnly={true}
        showBackLink={true}
        onBack={handleBack}
      />
    </Layout>
  );
}
